import { useEffect, useState } from 'react';
import { Check, Loader2, Wallet, CreditCard, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getPlans, subscribeToPlan, initiatePlanTopup, type Plan, type SubscribeRequest } from '@/api/services/plan';
import { formatPrice } from '@/utils/formatPrice';
import { apiClient } from '@/api/client';
import { ENDPOINTS, buildQueryString } from '@/api/endpoints';
import { getActiveSubscription, type ActiveSubscription, cancelSubscription } from '@/api/services/subscription';
import { getBalance } from '@/api/services/walletService';
import FeedbackModal from '@/components/FeedbackModal';
import ActionConfirmationModal from '@/components/ActionConfirmationModal';
import DonationCryptoPaymentModal from '@/components/DonationCryptoPaymentModal';

type PaymentMethod = 'wallet' | 'card' | 'crypto';
type CryptoSymbol = 'USDT' | 'USDC';
type CryptoChain = 'BSC_TESTNET' | 'SOLANA_DEVNET';
type CryptoPaymentDetails = {
  crypto: CryptoSymbol;
  chain: CryptoChain;
  baseFiat: string;
};
type PayerDetails = {
  email: string;
  phone: string;
  fullName: string;
};
type WalletBalanceSource = 'celo' | 'kxt' | 'fiat';
type WalletBalances = {
  celoBalance?: number | string;
  celoBalanceUSD?: number | string;
  ictBalance?: number | string;
  ictBalanceUSD?: number | string;
  fiatBalance?: number | string;
  kxtBalance?: number | string;
  kxtBalanceUSD?: number | string;
};
type CryptoPaymentInitData = {
  transactionId?: string;
  reference?: string;
  address?: string;
  network?: string;
  crypto?: string;
  baseFiat?: string;
  amountInCrypto?: number;
  platformFeeInCrypto?: number;
  amountInFiat?: number;
  validityPeriod?: number;
  expiresAt?: string;
  amount?: number;
  currency?: string;
};
// Subscriptions are network-wide — they're valid at any hotspot in the
// KonectX network. No hotspot ID needed for subscription creation.
// (Previously this was a hardcoded fake ID that doesn't exist in production.)
type TFunc = (key: string, options?: Record<string, unknown>) => string;
const DEFAULT_CRYPTO_DETAILS: CryptoPaymentDetails = {
  crypto: 'USDT',
  chain: 'BSC_TESTNET',
  baseFiat: 'USD'
};
const DEFAULT_PAYER_DETAILS: PayerDetails = {
  email: '',
  phone: '',
  fullName: ''
};
const DEFAULT_WALLET_SOURCE: WalletBalanceSource = 'fiat';
const WALLET_HOTSPOT_ID = '693817a3237e422ea638fc63';
const MaticIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 38.4 33.5" fill="currentColor">
    <path d="M29,10.2c-0.7-0.4-1.6-0.4-2.4,0L21,13.5l-3.8,2.1l-5.5,3.3c-0.7,0.4-1.6,0.4-2.4,0L5,16.3c-0.7-0.4-1.2-1.2-1.2-2.1v-5c0-0.8,0.4-1.6,1.2-2.1l4.3-2.5c0.7-0.4,1.6-0.4,2.4,0L16,7.2c0.7,0.4,1.2,1.2,1.2,2.1v3.3l3.8-2.2V7c0-0.8-0.4-1.6-1.2-2.1l-8-4.7c-0.7-0.4-1.6-0.4-2.4,0L1.2,5C0.4,5.4,0,6.2,0,7v9.4c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7c0.7,0.4,1.6,0.4,2.4,0l5.5-3.2l3.8-2.2l5.5-3.2c0.7-0.4,1.6-0.4,2.4,0l4.3,2.5c0.7,0.4,1.2,1.2,1.2,2.1v5c0,0.8-0.4,1.6-1.2,2.1L29,28.8c-0.7,0.4-1.6,0.4-2.4,0l-4.3-2.5c-0.7-0.4-1.2-1.2-1.2-2.1V21l-3.8,2.2v3.3c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7c0.7,0.4,1.6,0.4,2.4,0l8.1-4.7c0.7-0.4,1.2-1.2,1.2-2.1V17c0-0.8-0.4-1.6-1.2-2.1L29,10.2z"/>
  </svg>
);

const localizePlanName = (lang: string, name?: string) => {
  if (!name) return '';
  const key = name.trim().toLowerCase();
  const enMap: Record<string, string> = {
    basique: 'Basic',
    basic: 'Basic',
    standard: 'Standard',
    premium: 'Premium',
    prime: 'Premium',
    hebdomadaire: 'Weekly',
    weekly: 'Weekly'
  };
  const frMap: Record<string, string> = {
    basique: 'Basique',
    basic: 'Basique',
    standard: 'Standard',
    premium: 'Prime',
    prime: 'Prime',
    hebdomadaire: 'Hebdomadaire',
    weekly: 'Hebdomadaire'
  };
  return (lang === 'fr' ? frMap[key] : enMap[key]) || name;
};

const localizePeriod = (lang: string, period?: string) => {
  if (!period) return '';
  const key = period.trim().toLowerCase();
  if (lang === 'fr') {
    if (key === 'day') return 'jour';
    if (key === 'week') return 'semaine';
    if (key === 'month') return 'mois';
    if (key === 'daily') return 'jour';
    if (key === 'weekly') return 'semaine';
    if (key === 'monthly') return 'mois';
  } else {
    if (key === 'jour') return 'day';
    if (key === 'semaine') return 'week';
    if (key === 'mois') return 'month';
    if (key === 'daily') return 'day';
    if (key === 'weekly') return 'week';
    if (key === 'monthly') return 'month';
  }
  return period;
};

const normalizePeriod = (period?: string): 'day' | 'week' | 'month' | '' => {
  if (!period) return '';
  const key = period.trim().toLowerCase();
  if (key === 'day' || key === 'daily' || key === 'jour') return 'day';
  if (key === 'week' || key === 'weekly' || key === 'semaine') return 'week';
  if (key === 'month' || key === 'monthly' || key === 'mois') return 'month';
  return '';
};

const normalizePlanName = (name?: string) => (name || '').trim().toLowerCase();
const isPremiumPlanName = (name?: string) => {
  const normalized = normalizePlanName(name);
  return normalized === 'premium' || normalized === 'prime';
};
const isPlanLocked = (activeSubscription: ActiveSubscription | null, plan: Plan) => {
  if (!activeSubscription || activeSubscription.status !== 'active') {
    return false;
  }

  const activeIsPremium = isPremiumPlanName(activeSubscription.plan?.name);
  const nextIsPremium = isPremiumPlanName(plan.name);
  const activePeriodRank = getPeriodRank(activeSubscription.plan?.period);
  const nextPeriodRank = getPeriodRank(plan.period);

  if (activeIsPremium && !nextIsPremium) {
    return true;
  }

  if (activeIsPremium && nextIsPremium) {
    return nextPeriodRank < activePeriodRank;
  }

  if (!activeIsPremium && nextIsPremium) {
    return nextPeriodRank < activePeriodRank;
  }

  return false;
};

const getPeriodRank = (period?: string): number => {
  const normalized = normalizePeriod(period);
  if (normalized === 'day') return 0;
  if (normalized === 'week') return 1;
  if (normalized === 'month') return 2;
  return 99;
};

const localizeFeature = (lang: string, feature?: string) => {
  if (!feature) return '';
  let text = feature;
  if (lang === 'en') {
    text = text.replace(/Données illimitées/gi, 'Unlimited Data');
    text = text.replace(/Accès illimité/gi, 'Unlimited Access');
    text = text.replace(/(\d+)\s*Go de données/gi, '$1GB Data');
    text = text.replace(/(\d+)\s*GB de données/gi, '$1GB Data');
    text = text.replace(/Jusqu['\u2019]?\s*[aà]\s*(\d+)\s*appareils/gi, 'Up to $1 devices');
  } else {
    text = text.replace(/Unlimited Data/gi, 'Données illimitées');
    text = text.replace(/Unlimited Access/gi, 'Accès illimité');
    text = text.replace(/(\d+)\s*GB Data/gi, '$1 Go de données');
    text = text.replace(/Up to\s*(\d+)\s*devices/gi, 'Jusqu\u2019\u00e0 $1 appareils');
  }
  return text;
};

export default function PricingPlans() {
  const { i18n } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem('i18nextLng')?.startsWith('fr') ? 'fr' : 'en'
  );
  const t = i18n.getFixedT(langOverride);
  
  // --- Original States ---
  const [plans, setPlans] = useState<{
    daily: Plan[];
    weekly: Plan[];
    monthly: Plan[];
  } | null>(null);
  const [activeSubscription, setActiveSubscription] = useState<ActiveSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ type: 'success', title: '', message: '' });
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    plan: Plan | null;
    action: 'subscribe' | 'upgrade' | 'switch';
    mode?: 'period-change' | 'plan-change';
  }>({ plan: null, action: 'subscribe' });

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWalletBalanceModal, setShowWalletBalanceModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showCryptoPaymentModal, setShowCryptoPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [selectedWalletBalance, setSelectedWalletBalance] = useState<WalletBalanceSource>(DEFAULT_WALLET_SOURCE);
  const [walletBalances, setWalletBalances] = useState<WalletBalances | null>(null);
  const [isLoadingWalletBalances, setIsLoadingWalletBalances] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<Plan | null>(null);
  const [paymentFlow, setPaymentFlow] = useState<'subscribe' | 'switch' | 'topup'>('subscribe');
  const [cryptoVerifyFlow, setCryptoVerifyFlow] = useState<'subscribe' | 'topup' | null>(null);
  const [cryptoDetails, setCryptoDetails] = useState<CryptoPaymentDetails>(DEFAULT_CRYPTO_DETAILS);
  const [payerDetails, setPayerDetails] = useState<PayerDetails>(DEFAULT_PAYER_DETAILS);
  const [cryptoPaymentData, setCryptoPaymentData] = useState<CryptoPaymentInitData | null>(null);
  const [isVerifyingCryptoPayment, setIsVerifyingCryptoPayment] = useState(false);
  const [showFlutterwaveIframe, setShowFlutterwaveIframe] = useState(false);
  const [flutterwavePaymentLink, setFlutterwavePaymentLink] = useState<string | null>(null);
  
  const cryptoLiveMode = (import.meta.env.VITE_CRYPTO_LIVE as string | undefined) || 'live';
  const isCryptoTestMode = cryptoLiveMode.trim().toLowerCase() === 'test';

  // --- Logic Functions ---
  
  // --- Effects ---

  // 1. RECOVERY EFFECT: Grabs token from URL if it exists
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlToken = queryParams.get("token");

    if (urlToken && urlToken !== "null" && urlToken !== "undefined") {
      // Identity ADR: never store a token from the URL in localStorage, and never
      // synthesize a role on the client. The session is carried by the HttpOnly
      // cookie; we just strip the token from the URL bar and load from the server.
      window.history.replaceState({}, document.title, window.location.pathname);
      loadData();
    }
  }, []);

  // 2. INITIAL LOAD EFFECT
  // Only run if the recovery effect (#1 above) didn't already trigger a load.
  // The recovery effect handles URL-token case; this handles normal navigation.
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const hasUrlToken = !!queryParams.get("token");
    if (hasUrlToken) return; // Recovery effect already called loadData
    loadData();
  }, []);

  // 3. LANGUAGE EFFECT
  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      const normalized = storedLang.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith('fr') ? 'fr' : 'en';
      setLangOverride(normalized);
    };
    window.addEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener('appLanguageChanged', onAppLanguageChanged as EventListener);
    };
  }, [i18n]);

  const localizePlanNameLocal = (name?: string) => localizePlanName(langOverride, name);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [plansResponse, subscriptionResponse] = await Promise.all([
        getPlans(),
        getActiveSubscription()
      ]);
      
      setPlans(plansResponse.data);
      setActiveSubscription(subscriptionResponse.data);
    } catch (err: unknown) {
      console.error('Error loading data:', err);
      setError((err as {message?: string}).message || t('enduser.plan.errorMessages.loadFailed', { defaultValue: 'Failed to load plans' }));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubscription = async () => {
    console.log('switching plan', confirmationData)
    
    if (confirmationData.plan) {

      setShowConfirmation(false);
      setPendingPlan(confirmationData.plan);
      setPaymentFlow('switch');
      setSelectedPaymentMethod(null);
      setShowPaymentModal(true);
    }
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
    setPendingPlanId(null);
  };

  const handleSubscribe = async (plan: Plan) => {
    const action = getPlanAction(plan);

    if (action === 'current') {
      setFeedbackData({
        type: 'info',
        title: 'Current Plan',
        message: t('enduser.plan.messages.alreadyCurrent', {
          defaultValue: '{{name}} ({{period}}) is already your current plan.',
          name: plan.name,
          period: plan.period
        })
      });
      setShowFeedback(true);
      return;
    }

    if (action === 'topup') {
      setPendingPlan(plan);
      setPaymentFlow('topup');
      setSelectedPaymentMethod(null);
      setShowPaymentModal(true);
      return;
    }

    if (action === 'switch') {
      const isSameName = isSamePlanName(plan);
      setConfirmationData({ plan, action: 'switch', mode: isSameName ? 'period-change' : 'plan-change' });
      setShowConfirmation(true);
      return;
    }

    setPendingPlan(plan);
    setPaymentFlow('subscribe');
    setSelectedPaymentMethod(null);
    setShowPaymentModal(true);
  };

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
  };

  const updateCryptoDetails = (field: keyof CryptoPaymentDetails, value: string) => {
    setCryptoDetails((prev) => ({
      ...prev,
      [field]: value as CryptoPaymentDetails[typeof field]
    }));
  };

  const updatePayerDetails = (field: keyof PayerDetails, value: string) => {
    setPayerDetails((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const loadWalletBalances = async () => {
    try {
      setIsLoadingWalletBalances(true);
      const response = await getBalance();
      setWalletBalances(((response as unknown as Record<string, unknown>)?.data ?? response) as WalletBalances);
    } catch (err: unknown) {
      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.messages.failedTitle', { defaultValue: 'Subscription Failed' }),
        message: (err as {message?: string}).message || 'Failed to load wallet balances'
      });
      setShowFeedback(true);
    } finally {
      setIsLoadingWalletBalances(false);
    }
  };

  const getWalletPayload = () =>
    selectedPaymentMethod === 'wallet'
      ? {
          walletType: selectedWalletBalance,
          balanceType: selectedWalletBalance,
          walletSource: selectedWalletBalance
        }
      : {};

  const runPaymentFlow = async () => {
    if (!selectedPaymentMethod || !pendingPlan) return;
    if (selectedPaymentMethod === 'wallet' && !selectedWalletBalance) return;
    if (paymentFlow === 'topup' || paymentFlow === 'switch') {
      await runPlanTopup(pendingPlan, selectedPaymentMethod);
      return;
    }
    await initiatePlanSubscription(pendingPlan, selectedPaymentMethod);
  };

  const handlePaymentOptionsProceed = () => {
    if (!selectedPaymentMethod) return;
    if (selectedPaymentMethod === 'wallet') {
      setShowPaymentModal(false);
      void loadWalletBalances();
      setShowWalletBalanceModal(true);
      return;
    }
    setShowPaymentModal(false);
    setShowPaymentDetailsModal(true);
  };

  const handleWalletBalanceProceed = () => {
    setShowWalletBalanceModal(false);
    void runPaymentFlow();
  };

  const handlePaymentDetailsProceed = () => {
    setShowPaymentDetailsModal(false);
    void runPaymentFlow();
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setShowWalletBalanceModal(false);
    setSelectedPaymentMethod(null);
    setSelectedWalletBalance(DEFAULT_WALLET_SOURCE);
    setPendingPlan(null);
    setPaymentFlow('subscribe');
    setCryptoDetails(DEFAULT_CRYPTO_DETAILS);
    setPayerDetails(DEFAULT_PAYER_DETAILS);
  };

  const closeWalletBalanceModal = () => {
    setShowWalletBalanceModal(false);
    setShowPaymentModal(true);
  };

  const closePaymentDetailsModal = () => {
    setShowPaymentDetailsModal(false);
    setShowPaymentModal(true);
  };

  const closeCryptoPaymentModal = () => {
    setShowCryptoPaymentModal(false);
    setCryptoPaymentData(null);
    setCryptoVerifyFlow(null);
  };

  const handleCryptoPaymentVerify = async () => {
    const isTopupFlow = cryptoVerifyFlow === 'topup';
    const verifyValue = cryptoPaymentData?.reference;

    if (!verifyValue) {
      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.crypto.verifyFailedTitle', { defaultValue: 'Verification Failed' }),
        message: t('enduser.plan.crypto.verifyMissingRef', { defaultValue: 'Missing payment reference.' })
      });
      setShowFeedback(true);
      return;
    }

    try {
      setIsVerifyingCryptoPayment(true);
      const query = buildQueryString({ reference: verifyValue });
      const verifyEndpoint =
        isTopupFlow
          ? ENDPOINTS.PLAN.TOPUP_IVORYPAY_VERIFY
          : ENDPOINTS.PLAN.SUBSCRIBE_IVORYPAY_VERIFY;
      const response = await apiClient.get(`${verifyEndpoint}${query}`);
      const success = response?.data?.success;
      const message = response?.data?.message || (success ? 'Payment verified successfully.' : 'Payment verification failed.');

      if (success) {
        setFeedbackData({
          type: 'success',
          title: t('enduser.plan.crypto.verifySuccessTitle', { defaultValue: 'Payment Verified' }),
          message
        });
        setShowFeedback(true);
        closeCryptoPaymentModal();
        return;
      }

      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.crypto.verifyFailedTitle', { defaultValue: 'Verification Failed' }),
        message
      });
      setShowFeedback(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string }; message?: string } }; message?: string };
      const errorMessage =
        axiosErr.response?.data?.error?.message ||
        axiosErr.response?.data?.message ||
        axiosErr.message ||
        'Payment verification failed.';
      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.crypto.verifyFailedTitle', { defaultValue: 'Verification Failed' }),
        message: errorMessage
      });
      setShowFeedback(true);
    } finally {
      setIsVerifyingCryptoPayment(false);
    }
  };

  const closeFlutterwaveIframe = () => {
    setShowFlutterwaveIframe(false);
    setFlutterwavePaymentLink(null);
    setSelectedPaymentMethod(null);
    setPendingPlan(null);
    const planSuccess = localStorage.getItem('planSubscribeSuccess') === 'true';
    const topupSuccess = localStorage.getItem('planTopupSuccess') === 'true';
    const topupPending = localStorage.getItem('pendingPlanTopup') === 'true';
    if (!topupSuccess && topupPending) {
      localStorage.removeItem('pendingPlanTopup');
    }
    if (planSuccess || topupSuccess) {
      localStorage.removeItem('planSubscribeSuccess');
      localStorage.removeItem('planTopupSuccess');
      window.location.reload();
    }
  };

  const initiatePlanSubscription = async (plan: Plan, paymentMethod: PaymentMethod) => {
    try {
      
      if (paymentFlow === 'switch') {
        // ⚠️ KNOWN ISSUE: cancellation happens BEFORE the new subscription's
        // payment completes. If payment fails (declined card, network error),
        // the user is left without any active subscription. This will be
        // refactored in a future release with an atomic switch endpoint.
        // For now, the failure mode is "user must resubscribe" - recoverable
        // but bad UX. Tracked in production bug log.
        const response = await cancelSubscription(
          activeSubscription.subscriptionId,
          'switching plan',
          false
        );
        console.log(response, 'switch plan');
      }

      
      setSubscribingPlanId(plan.id);

     
      const previousSubscription = activeSubscription;

      const subscriptionPayload: SubscribeRequest = {
        paymentMethod,
        autoRenew: true,
        // hotspotId: WALLET_HOTSPOT_ID,
        ...getWalletPayload(),
        ...(paymentMethod === 'crypto'
          ? {
              planId: plan.id,
              crypto: cryptoDetails.crypto,
              chain: isCryptoTestMode ? 'BSC_TESTNET' : cryptoDetails.chain,
              baseFiat: cryptoDetails.baseFiat
            }
          : {})
      };

      const response = await subscribeToPlan(plan.id, subscriptionPayload);
      const paymentLink = response?.data?.paymentLink;

      if (paymentMethod === 'card') {
        if (!paymentLink) {
          throw new Error(t('enduser.plan.payment.initFailed', { defaultValue: 'Failed to initialize payment' }));
        }
        setFlutterwavePaymentLink(paymentLink);
        setShowFlutterwaveIframe(true);
        return;
      }

      if (paymentMethod === 'crypto') {
        const payload = response?.data as CryptoPaymentInitData | undefined;
        if (payload?.address) {
          setCryptoVerifyFlow('subscribe');
          setCryptoPaymentData(payload);
          setShowCryptoPaymentModal(true);
          return;
        }
      }

      const subscriptionResponse = await getActiveSubscription();
      setActiveSubscription(subscriptionResponse.data);

      const isPeriodChange =
        previousSubscription?.status === 'active' &&
        previousSubscription.plan.name === plan.name &&
        previousSubscription.plan.period !== plan.period;

      const successMessage = isPeriodChange
        ? `Your ${plan.name} plan billing has been changed from ${previousSubscription?.plan.period} to ${plan.period}.`
        : t('enduser.plan.messages.subscribed', {
            defaultValue: 'You have successfully subscribed to the {{name}} plan using {{method}}.',
            name: plan.name,
            method: paymentMethod
          });

      setFeedbackData({
        type: 'success',
        title: t('enduser.plan.messages.successTitle', { defaultValue: 'Subscription Successful!' }),
        message: successMessage
      });
      setShowFeedback(true);
      setSelectedPaymentMethod(null);
      setPendingPlan(null);
    } catch (err: unknown) {
      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.messages.failedTitle', { defaultValue: 'Subscription Failed' }),
        message: (err as {message?: string}).message || t('enduser.messages.failedMessage', { defaultValue: 'Failed to subscribe' })
      });
      setShowFeedback(true);
    } finally {
      setSubscribingPlanId(null);
      setPaymentFlow('subscribe');
    }
  };

  const runPlanTopup = async (plan: Plan, paymentMethod: PaymentMethod) => {
    try {
      if (paymentMethod !== 'wallet' && paymentMethod !== 'card' && paymentMethod !== 'crypto') {
        throw new Error('Unsupported payment method for top up');
      }

      setSubscribingPlanId(plan.id);

      const response = await initiatePlanTopup({
        planId: plan.id,
        paymentMethod,
        ...getWalletPayload(),
        ...(paymentMethod === 'crypto'
          ? {
              crypto: cryptoDetails.crypto,
              chain: isCryptoTestMode ? 'BSC_TESTNET' : cryptoDetails.chain,
              baseFiat: cryptoDetails.baseFiat
            }
          : {})
      });

      const paymentLink = response?.data?.paymentLink;

      if (paymentMethod === 'card') {
        if (!paymentLink) {
          throw new Error(t('enduser.plan.payment.initFailed', { defaultValue: 'Failed to initialize payment' }));
        }
        localStorage.setItem('pendingPlanTopup', 'true');
        setFlutterwavePaymentLink(paymentLink);
        setShowFlutterwaveIframe(true);
        return;
      }

      if (paymentMethod === 'crypto') {
        const payload = response?.data as CryptoPaymentInitData | undefined;
        if (payload?.address) {
          setCryptoVerifyFlow('topup');
          setCryptoPaymentData(payload);
          setShowCryptoPaymentModal(true);
          return;
        }
      }

      setFeedbackData({
        type: 'success',
        title: t('enduser.plan.messages.topupSuccessTitle', { defaultValue: 'Top up successful!' }),
        message: response?.message || t('enduser.plan.messages.topupSuccessBody', { defaultValue: 'Your plan has been topped up successfully.' })
      });
      setShowFeedback(true);
      setSelectedPaymentMethod(null);
      setPendingPlan(null);
    } catch (err: unknown) {
      setFeedbackData({
        type: 'error',
        title: t('enduser.plan.messages.topupFailedTitle', { defaultValue: 'Top up failed' }),
        message: (err as {message?: string}).message || t('enduser.messages.topupFailedBody', { defaultValue: 'Failed to top up plan' })
      });
      setShowFeedback(true);
    } finally {
      setSubscribingPlanId(null);
      setPaymentFlow('subscribe');
    }
  };

  const isActivePlan = (plan: Plan): boolean => {
    if (!activeSubscription || activeSubscription.status !== 'active') {
      return false;
    }
    if (activeSubscription.plan?.id && plan.id && activeSubscription.plan.id === plan.id) {
      return true;
    }
    const activePeriod = normalizePeriod(activeSubscription.plan?.period);
    const planPeriod = normalizePeriod(plan.period);
    return (
      normalizePlanName(activeSubscription.plan?.name) === normalizePlanName(plan.name) &&
      activePeriod !== '' &&
      activePeriod === planPeriod
    );
  };

  const isSamePlanName = (plan: Plan): boolean => {
    if (!activeSubscription || activeSubscription.status !== 'active') {
      return false;
    }
    return normalizePlanName(activeSubscription.plan.name) === normalizePlanName(plan.name);
  };

  const getPlansForPeriod = (period: Plan['period'] | string): Plan[] => {
    if (!plans) return [];
    const normalized = normalizePeriod(period);
    if (normalized === 'day') return plans.daily;
    if (normalized === 'week') return plans.weekly;
    if (normalized === 'month') return plans.monthly;
    return [];
  };

  const getPlanIndex = (plan: Plan, list: Plan[]): number =>
    list.findIndex(
      (p) =>
        (p.id && plan.id && p.id === plan.id) ||
        (normalizePlanName(p.name) === normalizePlanName(plan.name) &&
          normalizePeriod(p.period) === normalizePeriod(plan.period))
    );

  const canTopUpActivePlan = (plan: Plan): boolean =>
    isActivePlan(plan) && !isPremiumPlanName(plan.name);

  const getPlanAction = (plan: Plan): 'subscribe' | 'current' | 'topup' | 'switch' => {
    if (!activeSubscription || activeSubscription.status !== 'active') {
      return 'subscribe';
    }
    if (canTopUpActivePlan(plan)) {
      return 'topup';
    }
    if (isActivePlan(plan)) {
      return 'current';
    }

    const activePeriod = normalizePeriod(activeSubscription.plan.period);
    const planPeriod = normalizePeriod(plan.period);

    if (getPeriodRank(planPeriod) < getPeriodRank(activePeriod)) {
      return 'topup';
    }
    if (getPeriodRank(planPeriod) > getPeriodRank(activePeriod)) {
      return 'switch';
    }

    const activeList = getPlansForPeriod(activePeriod);
    const activePlanDetails =
      activeList.find((p) => p.id && activeSubscription.plan.id && p.id === activeSubscription.plan.id) ||
      activeList.find(
        (p) =>
          normalizePlanName(p.name) === normalizePlanName(activeSubscription.plan.name) &&
          normalizePeriod(p.period) === activePeriod
      );

    if (activePlanDetails && typeof activePlanDetails.price === 'number') {
      const planCompare = plan.price_minor ?? plan.price;
      const activeCompare = activePlanDetails.price_minor ?? activePlanDetails.price;
      return planCompare < activeCompare ? 'topup' : 'switch';
    }

    const orderedList = [...activeList].sort((a, b) => (a.price_minor ?? a.price ?? 0) - (b.price_minor ?? b.price ?? 0));
    const activeIndex = getPlanIndex(activePlanDetails ?? plan, orderedList);
    const planIndex = getPlanIndex(plan, orderedList);
    if (activeIndex !== -1 && planIndex !== -1) {
      return planIndex < activeIndex ? 'topup' : 'switch';
    }

    return 'switch';
  };

  const getButtonText = (plan: Plan): string => {
    if (
      activeSubscription &&
      activeSubscription.status === 'active' &&
      isPremiumPlanName(activeSubscription.plan?.name) &&
      !isPremiumPlanName(plan.name)
    ) {
      return t('enduser.plan.actions.premiumLocked', { defaultValue: 'Unavailable on Premium' });
    }

    if (
      activeSubscription &&
      activeSubscription.status === 'active' &&
      isPlanLocked(activeSubscription, plan)
    ) {
      if (isPremiumPlanName(plan.name)) {
        return t('enduser.plan.actions.lowerPremiumLocked', { defaultValue: 'Lower premium plan unavailable' });
      }
    }

    const action = getPlanAction(plan);
    if (action === 'subscribe') {
      return t('enduser.plan.actions.subscribe', { defaultValue: 'Subscribe' });
    }
    if (action === 'current') {
      return t('enduser.plan.actions.current', { defaultValue: 'Current Plan' });
    }
    if (action === 'topup') {
      return t('enduser.plan.actions.topUp', { defaultValue: 'Top up' });
    }
    return t('enduser.plan.actions.switch', { defaultValue: 'Switch Plan' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !plans) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || t('enduser.plan.errorMessages.loadFailed', { defaultValue: 'Failed to load plans' })}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            {t('enduser.plan.actions.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('enduser.plan.metaTitle', { defaultValue: 'Plans | KonectX' })}</title>
        <meta name="description" content={t('enduser.plan.metaDescription', { defaultValue: 'Choose the perfect plan for your WiFi needs' })} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('enduser.plan.Choose_A_Plan', { defaultValue: 'Choose a Plan' })}
              </h1>
              {activeSubscription && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('enduser.plan.currentplan', { defaultValue: 'Current plan' })}:{' '}
                  <span className="font-semibold">{localizePlanNameLocal(activeSubscription.plan.name)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Daily Plans */}
          <PlanSection
            title={t('enduser.plan.plantitle.0', { defaultValue: 'Daily Plans' })}
            plans={plans.daily}
            activeSubscription={activeSubscription}
            subscribingPlanId={subscribingPlanId}
            onSubscribe={handleSubscribe}
            isActivePlan={isActivePlan}
            getButtonText={getButtonText}
            t={t}
            lang={langOverride}
          />

          {/* Weekly Plans */}
          <PlanSection
            title={t('enduser.plan.plantitle.1', { defaultValue: 'Weekly Plans' })}
            plans={plans.weekly}
            activeSubscription={activeSubscription}
            subscribingPlanId={subscribingPlanId}
            onSubscribe={handleSubscribe}
            isActivePlan={isActivePlan}
            getButtonText={getButtonText}
            t={t}
            lang={langOverride}
          />

          {/* Monthly Plans */}
          <PlanSection
            title={t('enduser.plan.plantitle.2', { defaultValue: 'Monthly Plans' })}
            plans={plans.monthly}
            activeSubscription={activeSubscription}
            subscribingPlanId={subscribingPlanId}
            onSubscribe={handleSubscribe}
            isActivePlan={isActivePlan}
            getButtonText={getButtonText}
            t={t}
            lang={langOverride}
          />
        </div>
      </div>

     
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => {
          setShowFeedback(false);
          if (feedbackData.type === 'success') {
            window.location.reload();
          }
        }}
        type={feedbackData.type}
        title={feedbackData.title}
        message={feedbackData.message}
      />

 
      <ActionConfirmationModal
        isOpen={showConfirmation}
        onClose={closeConfirmation}
        onConfirm={handleConfirmSubscription}
        title={confirmationData.action === 'upgrade'
          ? t('enduser.plan.modals.upgradeTitle', { defaultValue: 'Upgrade Plan?' })
          : t('enduser.plan.modals.switchTitle', { defaultValue: 'Switch Plan?' })}
        message={
          activeSubscription && confirmationData.plan
            ? confirmationData.mode === 'period-change'
              ? t('enduser.plan.modals.periodChangeMessage', {
                  defaultValue: 'You are currently on the "{{currentName}}" plan with {{currentPeriod}} billing. Do you want to switch to {{nextPeriod}} billing?',
                  currentName: activeSubscription.plan.name,
                  currentPeriod: activeSubscription.plan.period,
                  nextPeriod: confirmationData.plan.period
                })
              : t('enduser.plan.modals.planChangeMessage', {
                  defaultValue: 'You currently have an active "{{currentName}}" subscription. Do you want to switch to "{{nextName}}"?',
                  currentName: activeSubscription.plan.name,
                  nextName: confirmationData.plan.name
                })
            : t('enduser.plan.modals.genericMessage', { defaultValue: 'Do you want to proceed with this subscription?' })
        }
        itemName={confirmationData.plan?.name}
        additionalInfo={t('enduser.plan.modals.note', { defaultValue: 'Note: This will cancel your current subscription and start a new one.' })}
        confirmText={confirmationData.action === 'upgrade'
          ? t('enduser.plan.modals.upgradeConfirm', { defaultValue: 'Upgrade Now' })
          : t('enduser.plan.modals.switchConfirm', { defaultValue: 'Switch Plan' })}
        variant={confirmationData.mode === 'period-change' ? 'info' : confirmationData.action === 'upgrade' ? 'info' : 'warning'}
        isLoading={subscribingPlanId === confirmationData.plan?.id}
      />


      <PaymentOptionsModal
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        selectedMethod={selectedPaymentMethod}
        onSelectMethod={handlePaymentMethodSelect}
        onProceed={handlePaymentOptionsProceed}
        plan={pendingPlan}
        mode={paymentFlow === 'topup' ? 'topup' : 'subscribe'}
        isProcessing={subscribingPlanId === pendingPlan?.id}
        t={t}
      />

      <PaymentDetailsModal
        isOpen={showPaymentDetailsModal}
        onClose={closePaymentDetailsModal}
        onProceed={handlePaymentDetailsProceed}
        plan={pendingPlan}
        method={selectedPaymentMethod}
        payerDetails={payerDetails}
        onPayerDetailsChange={updatePayerDetails}
        cryptoDetails={cryptoDetails}
        onCryptoDetailsChange={updateCryptoDetails}
        isCryptoTestMode={isCryptoTestMode}
        isProcessing={subscribingPlanId === pendingPlan?.id}
        t={t}
      />

      <WalletBalanceModal
        isOpen={showWalletBalanceModal}
        onClose={closeWalletBalanceModal}
        onProceed={handleWalletBalanceProceed}
        selectedSource={selectedWalletBalance}
        onSelectSource={setSelectedWalletBalance}
        balances={walletBalances}
        plan={pendingPlan}
        isLoading={isLoadingWalletBalances}
        isProcessing={subscribingPlanId === pendingPlan?.id}
        t={t}
      />

      <CryptoPaymentModal
        isOpen={showCryptoPaymentModal}
        onClose={closeCryptoPaymentModal}
        onPaid={handleCryptoPaymentVerify}
        isVerifying={isVerifyingCryptoPayment}
        data={cryptoPaymentData}
        t={t}
      />

   
      <FlutterwaveIframeModal
        isOpen={showFlutterwaveIframe}
        paymentLink={flutterwavePaymentLink}
        onClose={closeFlutterwaveIframe}
        isProcessing={subscribingPlanId === pendingPlan?.id}
        t={t}
      />
    </>
  );
}

interface PaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  plan: Plan | null;
  method: PaymentMethod | null;
  payerDetails: PayerDetails;
  onPayerDetailsChange: (field: keyof PayerDetails, value: string) => void;
  cryptoDetails: CryptoPaymentDetails;
  onCryptoDetailsChange: (field: keyof CryptoPaymentDetails, value: string) => void;
  isCryptoTestMode: boolean;
  isProcessing: boolean;
  t: TFunc;
}

interface WalletBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  selectedSource: WalletBalanceSource;
  onSelectSource: (source: WalletBalanceSource) => void;
  balances: WalletBalances | null;
  plan: Plan | null;
  isLoading: boolean;
  isProcessing: boolean;
  t: TFunc;
}

function WalletBalanceModal({
  isOpen,
  onClose,
  onProceed,
  selectedSource,
  onSelectSource,
  balances,
  plan,
  isLoading,
  isProcessing,
  t
}: WalletBalanceModalProps) {
  if (!isOpen) return null;

  const options: Array<{
    id: WalletBalanceSource;
    title: string;
    amount: number | string | undefined;
    approx?: number | string | undefined;
    helper: string;
  }> = [
    {
      id: 'celo',
      title: 'Celo Balance',
      amount: balances?.celoBalance,
      approx: balances?.celoBalanceUSD,
      helper: 'Use your Celo wallet balance'
    },
    {
      id: 'kxt',
      title: 'KXT Balance',
      amount: balances?.kxtBalance,
      approx: balances?.kxtBalanceUSD,
      helper: 'Use your KXT wallet balance'
    },
    {
      id: 'fiat',
      title: 'Fiat Balance',
      amount: balances?.fiatBalance,
      helper: 'Use your fiat wallet balance'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t('enduser.plan.payment.walletSourceTitle', { defaultValue: 'Choose Wallet Balance' })}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('enduser.plan.payment.walletSourceDesc', { defaultValue: 'Select which balance to use for this purchase.' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {plan && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              {t('enduser.plan.payment.selectedPlan', { defaultValue: 'Selected Plan' })}
            </p>
            <div className="flex items-center justify-between mt-1 gap-2">
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {plan.name}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600 whitespace-nowrap">
                {formatPrice(plan.price_minor, plan.currency, plan.price)}<span className="text-xs sm:text-sm font-normal">/{plan.period}</span>
              </p>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 space-y-3">
          {isLoading ? (
            <div className="py-10 flex items-center justify-center text-gray-600 dark:text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span>{t('enduser.plan.payment.loadingBalances', { defaultValue: 'Loading balances...' })}</span>
            </div>
          ) : (
            options.map((option) => {
              const isSelected = selectedSource === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => onSelectSource(option.id)}
                  className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                        {option.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {option.helper}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {option.amount ?? '0'}
                    </p>
                    {option.approx !== undefined && (
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Approx. ${option.approx} USD
                      </p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
          >
            {t('enduser.plan.actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={onProceed}
            disabled={isLoading || isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm sm:text-base">{t('enduser.plan.payment.processing', { defaultValue: 'Processing...' })}</span>
              </>
            ) : (
              <span className="text-sm sm:text-base">{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentDetailsModal({
  isOpen,
  onClose,
  onProceed,
  plan,
  method,
  payerDetails,
  onPayerDetailsChange,
  cryptoDetails,
  onCryptoDetailsChange,
  isCryptoTestMode,
  isProcessing,
  t
}: PaymentDetailsModalProps) {
  if (!isOpen || !plan || !method || method === 'wallet') return null;

  const [amount, setAmount] = useState('');
  const [validationError, setValidationError] = useState('');

  const isCryptoSelected = method === 'crypto';
  
  const handleProceed = () => {
    setValidationError('');

    if (!payerDetails.email.trim()) {
      return setValidationError('Email Address is required.');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerDetails.email.trim())) {
      return setValidationError('Please enter a valid email address.');
    }
    if (payerDetails.phone.trim().length < 5) {
      return setValidationError('Phone number must be at least 5 characters long.');
    }
    if (payerDetails.fullName.trim().length < 2) {
      return setValidationError('Full name must be at least 2 characters long.');
    }
    if (isCryptoSelected) {
      if (!cryptoDetails.crypto) return setValidationError('Please select a crypto currency.');
      if (!cryptoDetails.chain) return setValidationError('Please select a network chain.');
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        return setValidationError('Amount must be a valid number greater than 0.');
      }
    }

    onProceed();
  };

  useEffect(() => {
    if (!isOpen || !plan) return;
    const planAmount = plan.price_minor !== undefined && plan.currency
      ? Number((plan.price_minor / 100).toFixed(2))
      : Number(plan.price);
    setAmount(Number.isFinite(planAmount) ? planAmount.toFixed(2) : String(plan.price ?? ''));
  }, [isOpen, plan?.id, plan?.price, plan?.price_minor]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] w-[92vw] max-w-[600px] max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('enduser.plan.payment.completeTitle', { defaultValue: 'Complete Payment' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {validationError && (
          <div className="px-4 sm:px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/30">
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              {validationError}
            </p>
          </div>
        )}

        <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('enduser.plan.payment.planLabel', { defaultValue: 'Plan' })}</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {plan.name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('enduser.plan.payment.amountLabel', { defaultValue: 'Amount' })}</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {formatPrice(plan.price_minor, plan.currency, plan.price)}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.emailLabel', { defaultValue: 'Email Address' })}
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              value={payerDetails.email}
              onChange={(e) => onPayerDetailsChange('email', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.phoneLabel', { defaultValue: 'Phone Number' })}
            </label>
            <input
              type="tel"
              placeholder="+234 xxx xxx xxxx"
              value={payerDetails.phone}
              onChange={(e) => onPayerDetailsChange('phone', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('enduser.plan.payment.fullNameLabel', { defaultValue: 'Full Name' })}
            </label>
            <input
              type="text"
              placeholder="John Doe"
              value={payerDetails.fullName}
              onChange={(e) => onPayerDetailsChange('fullName', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {isCryptoSelected && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 bg-gray-50 dark:bg-[#252628] space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Crypto
                  </label>
                  <select
                    value={cryptoDetails.crypto}
                    onChange={(e) => onCryptoDetailsChange('crypto', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USDT">USDT</option>
                    <option value="USDC">USDC</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Chain
                  </label>
                  <select
                    value={cryptoDetails.chain}
                    onChange={(e) => onCryptoDetailsChange('chain', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BSC_TESTNET">BSC Testnet</option>
                  
                    <option value="SOLANA_DEVNET">SOLANA_DEVNET</option>
                    {/* <option value="POLYGON">Polygon</option> */}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {isCryptoTestMode && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Test mode enabled. Chain will be sent as BSC_TESTNET.
                </p>
              )}
            </div>
          )}

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {isCryptoSelected
                    ? t("enduser.plan.payment.secureCryptoTitle", { defaultValue: "Secure Payment via Crypto" })
                    : t("enduser.plan.payment.secureTitle", { defaultValue: "Secure Payment via Flutterwave" })}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {isCryptoSelected
                    ? t("enduser.plan.payment.secureCryptoDesc", { defaultValue: "We support USDT and USDC across multiple networks" })
                    : t("enduser.plan.payment.secureDesc", { defaultValue: "We support Credit Cards, Debit Cards, and Mobile Money" })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('enduser.plan.actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={handleProceed}
            disabled={isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-sm sm:text-base">{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface PaymentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMethod: PaymentMethod | null;
  onSelectMethod: (method: PaymentMethod) => void;
  onProceed: () => void;
  plan: Plan | null;
  mode: 'subscribe' | 'topup';
  isProcessing: boolean;
  t: TFunc;
}

function PaymentOptionsModal({
  isOpen,
  onClose,
  selectedMethod,
  onSelectMethod,
  onProceed,
  plan,
  mode,
  isProcessing,
  t
}: PaymentOptionsModalProps) {
  if (!isOpen) return null;

  const paymentMethods = [
    {
      id: 'wallet' as PaymentMethod,
      name: t('enduser.plan.payment.methods.wallet', { defaultValue: 'Wallet' }),
      description: t('enduser.plan.payment.methods.walletDesc', { defaultValue: 'Pay using your wallet balance' }),
      icon: Wallet
    },
    {
      id: 'card' as PaymentMethod,
      name: t('enduser.plan.payment.methods.card', { defaultValue: 'Credit, Debit Card or Mobile Money' }),
      description: t('enduser.plan.payment.methods.cardDesc', { defaultValue: 'Pay with debit, credit card or mobile money' }),
      icon: CreditCard
    },
    {
      id: 'crypto' as PaymentMethod,
      name: t('enduser.plan.payment.methods.crypto', { defaultValue: 'Crypto Payment' }),
      description: t('enduser.plan.payment.methods.cryptoDesc', { defaultValue: 'Pay with USDT/USDC' }),
      icon: MaticIcon
    }
  ];
  const visibleMethods = paymentMethods.filter((method) => {
    if (method.id === 'crypto' && plan && plan.price !== undefined) {
      const priceVal = Number(plan.price);
      if (!Number.isNaN(priceVal) && priceVal >= 2 && priceVal <= 5) {
        return false;
      }
    }
    return true;
  });
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t('enduser.plan.payment.chooseOption', { defaultValue: 'Choose Payment Option' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Plan Info */}
        {plan && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('enduser.plan.payment.selectedPlan', { defaultValue: 'Selected Plan' })}</p>
            <div className="flex items-center justify-between mt-1 gap-2">
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                {plan.name}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600 whitespace-nowrap">
                {formatPrice(plan.price_minor, plan.currency, plan.price)}<span className="text-xs sm:text-sm font-normal">/{plan.period}</span>
              </p>
            </div>
          </div>
        )}

        {/* Payment Methods */}
        <div className="p-4 sm:p-6 space-y-3">
          {visibleMethods.map((method) => {
            const Icon = method.icon;
            const isSelected = selectedMethod === method.id;
            
            return (
              <button
                key={method.id}
                onClick={() => onSelectMethod(method.id)}
                className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate">
                        {method.name}
                      </h3>
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {method.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
          >
            {t('enduser.plan.actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={onProceed}
            disabled={!selectedMethod || isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm sm:text-base">{t('enduser.plan.payment.processing', { defaultValue: 'Processing...' })}</span>
              </>
            ) : (
              <span className="text-sm sm:text-base">{t('enduser.plan.actions.proceed', { defaultValue: 'Proceed to Payment' })}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function CryptoPaymentModal({
  isOpen,
  onClose,
  onPaid,
  isVerifying,
  data
}: { isOpen: boolean; onClose: () => void; onPaid: () => void; isVerifying: boolean; data: CryptoPaymentInitData | null; t?: unknown }) {
  return (
    <DonationCryptoPaymentModal
      isOpen={isOpen}
      onClose={onClose}
      onPaid={onPaid}
      isVerifying={isVerifying}
      data={
        data
          ? {
              ...data,
              message: "Send payment to the address below to complete your transaction.",
            }
          : null
      }
    />
  );
}

interface FlutterwaveIframeModalProps {
  isOpen: boolean;
  paymentLink: string | null;
  onClose: () => void;
  isProcessing: boolean;
  t: TFunc;
}

function FlutterwaveIframeModal({
  isOpen,
  paymentLink,
  onClose,
  isProcessing,
  t
}: FlutterwaveIframeModalProps) {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]');

  useEffect(() => {
    if (!isOpen || !paymentLink || !isLocalhost) return;
    window.location.assign(paymentLink);
  }, [isOpen, paymentLink, isLocalhost]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-neutral-200/90 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f2f2f2] w-[92vw] max-w-[420px] max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-neutral-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-[#f7f7f7]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-neutral-900 flex items-center justify-center">
              <span className="text-white text-sm font-semibold">KX</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">KonectX</p>
              <p className="text-xs text-neutral-500">{t('enduser.plan.payment.secureCheckout', { defaultValue: 'Secure Checkout' })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-200 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex-1 bg-[#f2f2f2]">
          {paymentLink ? (
            isLocalhost ? (
              <div className="h-[70vh] flex items-center justify-center p-6 text-center">
                <div className="space-y-3">
                  <p className="text-sm text-neutral-700">
                    {t('enduser.plan.payment.localhostRedirect', {
                      defaultValue: 'Localhost detected. Opening payment page in your browser.'
                    })}
                  </p>
                  <button
                    onClick={() => window.location.assign(paymentLink)}
                    disabled={isProcessing}
                    className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm hover:bg-neutral-800 transition-colors disabled:opacity-50"
                  >
                    {t('enduser.plan.payment.openPaymentPage', { defaultValue: 'Open Payment Page' })}
                  </button>
                </div>
              </div>
            ) : (
              <iframe
                title={t('enduser.plan.payment.flutterwaveTitle', { defaultValue: 'Flutterwave Payment' })}
                src={paymentLink}
                className="w-full h-[70vh] border-0 bg-[#f2f2f2]"
                allow="payment"
              />
            )
          ) : (
            <div className="h-[70vh] flex items-center justify-center">
              <p className="text-sm text-neutral-600">{t('enduser.plan.payment.loadingLink', { defaultValue: 'Loading payment link...' })}</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

  interface PlanSectionProps {
  title: string;
  plans: Plan[];
  activeSubscription: ActiveSubscription | null;
  subscribingPlanId: string | null;
  onSubscribe: (plan: Plan) => void;
  isActivePlan: (plan: Plan) => boolean;
  getButtonText: (plan: Plan) => string;
  t: TFunc;
  lang: string;
}

function PlanSection({
  title,
  plans,
  activeSubscription,
  subscribingPlanId,
  onSubscribe,
  isActivePlan,
  getButtonText,
  t,
  lang
}: PlanSectionProps) {
  return (
    <div className="mb-12">
      <h2 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
        {title}
      </h2>
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isActive={isActivePlan(plan)}
            isDisabled={isPlanLocked(activeSubscription, plan)}
            isProcessing={subscribingPlanId === plan.id}
            buttonText={getButtonText(plan)}
            onSubscribe={() => onSubscribe(plan)}
            t={t}
            lang={lang}
          />
        ))}
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: Plan;
  isActive: boolean;
  isDisabled: boolean;
  isProcessing: boolean;
  buttonText: string;
  onSubscribe: () => void;
  t: TFunc;
  lang: string;
}

function PlanCard({ plan, isActive, isDisabled, isProcessing, buttonText, onSubscribe, t, lang }: PlanCardProps) {
  return (
    <div className="bg-white dark:bg-[#333436] rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow border-2 border-gray-300 dark:border-gray-600 min-h-[280px] flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {localizePlanName(lang, plan.name)}
        </h3>
        {isActive && (
          <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
            {t('enduser.plan.actions.active', { defaultValue: 'Active' })}
          </span>
        )}
      </div>
      
      <div className="mb-4">
        <span className="text-3xl font-bold text-gray-900 dark:text-white">
          {formatPrice(plan.price_minor, plan.currency, plan.price)}
        </span>
        <span className="text-sm text-gray-600 dark:text-gray-400"> /{localizePeriod(lang, plan.period)}</span>
      </div>
      
      <button
        onClick={onSubscribe}
        disabled={isProcessing || isDisabled}
        className={`w-full py-2 rounded-lg text-sm font-medium transition-colors mb-4 border ${
          isDisabled
            ? 'bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-500'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {buttonText}
          </span>
        ) : (
          buttonText
        )}
      </button>
      
      <div className="space-y-2 flex-grow">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <Check className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-300 flex-shrink-0" />
            <span>{localizeFeature(lang, feature)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}